import { SelfHostedIntegrationRuntime } from 'app/model/integrationRuntime/selfhosted.integrationRuntime';
import { stringIsNullOrEmpty } from './string.util';
import { Model } from 'app/model/backendmodel';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import { IntegrationRuntimeOperationsService } from 'app/apis/integration-runtime-operations-api.service';
import { LoggerService } from 'app/logger/logger.service';
import { GlobalVariables } from 'app/services/globals';
import { IValidationContext } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export const trinoSHIRVersion = '-1'; // TODO: update it to the correct version after the target IR is released.
export const azureTableOrFileMIAuthSHIRVersion = '5.44.8969.2';
export const opdgInPipelineVersion = '3000.214.2';

// Minimum OPDG (on-premises data gateway) version that supports SAP Table source auto-partitioning.
// Auto-partition (partitionOption = AutoDetect -> EnableAutoPartitioning) is only honored by gateways
// at or above 3000.326.0, so we only default it on when every involved OPDG meets this bar.
export const opdgSapTableAutoPartitionVersion = '3000.326.0';

export const restEnableCertVersion = '5.44.8969.2';
export const spoListEnableCertVersion = '5.45.8999.1';
export const oracleV2PrecisionAndScaleVersion = '5.56.0';
export const azurePostgreSQLV2ScriptandUpsertVersion = '5.53.0';

export interface ISelfHostedIRValidationResult {
    isSupported: boolean;
    runtimeIR: SelfHostedIntegrationRuntime;
}

export function compareDetailVersion(version1: string, version2: string, maxCompareLevel: number = 10): number {
    if (stringIsNullOrEmpty(version1) && stringIsNullOrEmpty(version2)) {
        return 0;
    }
    if (stringIsNullOrEmpty(version1)) {
        return -1;
    }
    if (stringIsNullOrEmpty(version2)) {
        return 1;
    }
    const version1Number = version1.split('.').map((value) => parseInt(value, 10));
    const version2Number = version2.split('.').map((value) => parseInt(value, 10));

    let compareLevel: number = 0;
    while (compareLevel < maxCompareLevel) {
        if (version1Number.length <= compareLevel && version2Number.length <= compareLevel) {
            return 0;
        }
        if (version1Number.length <= compareLevel) {
            return -1;
        }
        if (version2Number.length <= compareLevel) {
            return 1;
        }
        const version1NumberCurrentLevel = version1Number[compareLevel];
        const version2NumberCurrentLevel = version2Number[compareLevel];
        if (version1NumberCurrentLevel > version2NumberCurrentLevel) {
            return 1;
        }
        if (version1NumberCurrentLevel < version2NumberCurrentLevel) {
            return -1;
        }
        compareLevel = compareLevel + 1;
    }
    return 0;
}

export function getMinVersion(versions: string[], maxCompareLevel: number = 10): string {
    return versions.sort((a, b) => {
        return compareDetailVersion(a, b, maxCompareLevel);
    })[0];
}

export async function validateIRVersion(integrationRuntime: IntegrationRuntime, minIRVersion: string, maxCompareLevel: number = 10, context: IValidationContext): Promise<boolean> {
    try {
        if (integrationRuntime == null || integrationRuntime instanceof ManagedIntegrationRuntime || GlobalVariables.isCmdApiApp) {
            return true;
        }
        if (integrationRuntime instanceof SelfHostedIntegrationRuntime) {
            if (compareDetailVersion(integrationRuntime.version, minIRVersion, maxCompareLevel) < 0) {
                const irApi = await getServiceInstance<IntegrationRuntimeOperationsService>(context.contextData, 'IntegrationRuntimeOperationsService');
                const irStatus = await irApi.getStatusAsync(integrationRuntime.name);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const irTypeProp = (irStatus.properties as any).typeProperties as Model.SelfHostedIntegrationRuntimeStatusTypeProperties;
                // if ir doesn't have a version, it is invalid or offline, will return false
                if (irTypeProp == null || irTypeProp.version == null) {
                    return false;
                }
                integrationRuntime.version = irTypeProp.version;
                return compareDetailVersion(integrationRuntime.version, minIRVersion, maxCompareLevel) >= 0;
            } else {
                return true;
            }
        }
        return false;
    } catch (e) {
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        logger.warn('validateIRVersion-' + minIRVersion, e);
        // if any error happens in getStatusAsync, return true, we don't want any API failure to block further operation.
        return true;
    }
}


